import React, { useEffect, useState } from 'react';
import { reviewsAPI } from '../api/api';
import { ReviewForm } from './Reviews';
import { Star, X, Sparkle } from '@phosphor-icons/react';

/**
 * Homeowner dashboard prompt card — nudges homeowner to leave a review for
 * recently completed projects. Auto-dismisses on submit.
 */
export default function ReviewPromptCard() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePrompt, setActivePrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('review_prompts_dismissed') || '[]');
    } catch {
      return [];
    }
  });

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const res = await reviewsAPI.pendingPrompts();
      setPrompts(res.data?.prompts || []);
    } catch (err) {
      // Silently fail — dashboard prompts are optional
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPrompts(); }, []);

  const dismissPrompt = (projectId) => {
    const updated = [...dismissed, projectId];
    setDismissed(updated);
    localStorage.setItem('review_prompts_dismissed', JSON.stringify(updated));
  };

  const handleSuccess = () => {
    setActivePrompt(null);
    fetchPrompts();
  };

  const visible = prompts.filter(p => !dismissed.includes(p.project_id));

  if (loading || visible.length === 0) return null;

  return (
    <>
      <div
        className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-5 mb-6"
        data-testid="review-prompt-card"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow">
            <Sparkle size={22} weight="fill" className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-black text-neutral-900 dark:text-white text-lg">How was your project?</h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              Share your experience to help other homeowners pick the right pro.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {visible.slice(0, 3).map(prompt => (
            <div
              key={prompt.project_id}
              className="flex items-center justify-between gap-3 bg-white dark:bg-neutral-900 border border-amber-200 dark:border-amber-800 rounded-lg p-3"
              data-testid={`review-prompt-${prompt.project_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-neutral-900 dark:text-white truncate">{prompt.title}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                  with <span className="font-semibold">{prompt.reviewee_company || prompt.reviewee_name}</span>
                </p>
              </div>
              <button
                onClick={() => setActivePrompt(prompt)}
                className="flex items-center gap-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-lg transition-all whitespace-nowrap"
                data-testid={`leave-review-btn-${prompt.project_id}`}
              >
                <Star size={14} weight="fill" /> Leave Review
              </button>
              <button
                onClick={() => dismissPrompt(prompt.project_id)}
                className="text-neutral-400 hover:text-neutral-700 dark:hover:text-white p-1"
                data-testid={`dismiss-review-prompt-${prompt.project_id}`}
                title="Dismiss"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {activePrompt && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setActivePrompt(null)}
          data-testid="review-modal-overlay"
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-700 px-6 py-4">
              <h2 className="font-black text-lg text-neutral-900 dark:text-white">
                Review {activePrompt.reviewee_company || activePrompt.reviewee_name}
              </h2>
              <button
                onClick={() => setActivePrompt(null)}
                className="text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
                data-testid="close-review-modal-btn"
              >
                <X size={22} weight="bold" />
              </button>
            </div>
            <div className="p-6">
              <ReviewForm
                userId={activePrompt.reviewee_id}
                userName={activePrompt.reviewee_company || activePrompt.reviewee_name}
                projectId={activePrompt.project_id}
                jobTitle={activePrompt.title}
                onSuccess={handleSuccess}
                onCancel={() => setActivePrompt(null)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
